import { useEffect, useRef } from "react";
import classNames from "classnames";
import withStyles from "isomorphic-style-loader/withStyles";
import { ScaleLoader } from "react-spinners";
import BaseInput from "../../../../toolkit/baseInput";
import MarkdownContent from "../../../../toolkit/markdownContent";
import PrimaryButton from "../../../../toolkit/primaryButton";
import IGlobalConfig from "../../../../types/IGlobalConfig";
import { useChatSessionThread } from "./useChatSessionThread";
import s from "./styles.scss";

interface Props {
  config: IGlobalConfig;
  serviceAccessToken: string;
  charityId?: string;
  sessionId?: string;
  isDraft?: boolean;
  onSessionCreated?: () => Promise<void> | void;
}

function ChatSessionThread({
  config,
  serviceAccessToken,
  charityId,
  sessionId,
  isDraft = false,
  onSessionCreated,
}: Props) {
  const {
    messages,
    message,
    loading,
    error,
    sendLoading,
    sendError,
    handleMessageChange,
    handleSubmit,
  } = useChatSessionThread({
    config,
    serviceAccessToken,
    charityId,
    sessionId,
    isDraft,
    onSessionCreated,
  });
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  if (!sessionId) {
    return (
      <div className={s.threadEmpty}>
        Select a conversation or start a new one with +
      </div>
    );
  }

  if (loading) {
    return (
      <div className={s.threadLoading}>
        <ScaleLoader color="#1f65b1" height={24} width={4} loading />
      </div>
    );
  }

  if (error) {
    return <div className={s.threadError}>{error}</div>;
  }

  return (
    <>
      <div className={s.messages}>
        {messages.length === 0 && isDraft && (
          <div className={s.draftHint}>Send a message to start this chat</div>
        )}
        {messages.map(entry => (
          <div
            key={entry.id}
            className={classNames(
              s.bubble,
              entry.role === "user" ? s.bubbleUser : s.bubbleAssistant
            )}
          >
            {entry.role === "assistant" ? (
              <MarkdownContent content={entry.content} />
            ) : (
              entry.content
            )}
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>
      <form className={s.composer} onSubmit={handleSubmit}>
        <div className={s.inputWrapper}>
          <BaseInput
            type="text"
            name="chatSessionMessage"
            id="chatSessionMessageInput"
            value={message}
            onChange={handleMessageChange}
            onFocus={() => {}}
            placeholder={
              isDraft ? "Type a message..." : "Type a follow-up message..."
            }
            disabled={sendLoading}
            dataQAId="chatSessionMessage"
            dataElmId="chatSessionMessage"
            errors={sendError ? [sendError] : []}
            options={[]}
            customClassName={s.input}
          />
        </div>
        <PrimaryButton
          type="submit"
          disabled={!message.trim() || sendLoading}
          loading={sendLoading}
          loadingText="Thinking"
          className={s.submitButton}
        >
          Send
        </PrimaryButton>
      </form>
    </>
  );
}

export default withStyles(s)(ChatSessionThread);
